import styled from '@emotion/styled';
import { StripeProducts } from '@racemap/sdk/schema/billing';
import { EventTypes } from '@racemap/sdk/schema/events';
import { RacemapColors } from '@racemap/utilities/consts/common';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { Button, Card, type CollapseProps, Flex, Modal, Space, Typography } from 'antd';
import { type FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useIsTabletOrBelow } from 'src/lib/customHooks';
import { upTo } from 'src/lib/customHooks/useBreakpoint';
import { getEventTypeDocuLink } from 'src/lib/EventUtils';
import { DocuLink } from '../BasicComponents';
import {
  IconContestGroup,
  IconPlus,
  IconRegular,
  IconSafemap,
  IconStageGroup,
} from '../BasicComponents/Icons';
import { ProductPrice } from '../BasicComponents/ProductPrice/ProductPrice';

const { Text } = Typography;

interface Props {
  buttonType?: 'primary' | 'default' | 'ghost';
  buttonSize?: 'large' | 'middle' | 'small';
  compact?: boolean;
  // to avoid that the modal opens twice from the two implementations of CreateMapButton
  // in TrackingMapsListView and MainHeader.
  openIfCreateEventUrl?: boolean;
}

export const CreateMapButton: FC<Props> = ({
  buttonType = 'primary',
  compact,
  buttonSize = 'large',
  openIfCreateEventUrl = false,
}) => {
  const [open, setOpen] = useState(false);
  const isTabletOrBelow = useIsTabletOrBelow();
  const location = useLocation();
  const [selectedEventType, setSelectedEventType] = useState<EventTypes>(EventTypes.REGULAR);
  const navigate = useNavigate();
  const finalButtonSize = buttonSize || (isTabletOrBelow ? 'middle' : 'large');
  const finalButtonType = buttonType === 'ghost' ? 'primary' : buttonType;
  const isGhost = buttonType === 'ghost';
  const isCreateEventUrl = location.pathname === '/admin/events/create';

  useEffect(() => {
    if (isCreateEventUrl && !open && openIfCreateEventUrl) {
      setOpen(true);
      navigate('/admin/events', { replace: true });
    }
  }, [isCreateEventUrl, openIfCreateEventUrl, navigate, open, setOpen]);

  useEffect(() => {
    if (open) {
      setSelectedEventType(EventTypes.REGULAR);
    }
  }, [open]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handleButtonClick = () => {
    setOpen(true);
  };

  const handleCreateClick = () => {
    switch (selectedEventType) {
      case EventTypes.REGULAR:
        navigate('/admin/events/new');
        break;
      case EventTypes.CONTEST_GROUP:
        navigate('/admin/contestgroups/new');
        break;
      case EventTypes.STAGE_GROUP:
        navigate('/admin/stagegroups/new');
        break;
      case EventTypes.SAFEMAP:
        navigate('/admin/safemaps/new');
        break;
    }
    setOpen(false);
  };

  return (
    <>
      <Button
        type={finalButtonType}
        ghost={isGhost}
        icon={<IconPlus />}
        size={finalButtonSize}
        onClick={handleButtonClick}
        id={TestSelectors.admin.nav.buttons.newEvent.toString()}
      >
        {compact ? 'Map' : 'Create Map'}
      </Button>
      <Modal
        open={open}
        onCancel={handleCancel}
        title="Create New Tracking Map"
        footer={null}
        width={800}
      >
        <Space
          orientation="vertical"
          size="middle"
          style={{ width: '100%' }}
          id={TestSelectors.admin.nav.modals.createNewEventModal.toString()}
        >
          <Text>
            Select the map type that fits your event’s needs. You can create and test multiple maps.{' '}
            <Text strong>This step is completely free — no charges apply.</Text> Costs only occur
            when you later choose to activate your event.
          </Text>
          <GridWrapper>
            {items.map((item) => (
              <EventTypeCard
                key={item.key}
                label={item.label}
                buttonText={item.buttonText}
                selected={selectedEventType === item.key}
                onClick={() => setSelectedEventType(item.key as EventTypes)}
                content={item.children}
                onClickCreate={handleCreateClick}
              />
            ))}
          </GridWrapper>
        </Space>
      </Modal>
    </>
  );
};

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;

  @media ${upTo('phone')} {
    grid-template-columns: 1fr;
  }
`;

const EventTypeCard: FC<{
  label: React.ReactNode;
  buttonText: string;
  selected: boolean;
  onClick: () => void;
  content: React.ReactNode;
  onClickCreate: () => void;
}> = ({ label, buttonText, selected, onClick, content, onClickCreate }) => (
  <CardWrapper selected={selected} onClick={onClick}>
    <Card title={label} hoverable>
      <CardContentWrapper>
        <TextWrapper>{content}</TextWrapper>
        <CreateButton
          type="primary"
          onClick={onClickCreate}
          disabled={!selected}
          className={TestSelectors.admin.nav.buttons.newEventModal.toString()}
        >
          {buttonText}
        </CreateButton>
      </CardContentWrapper>
    </Card>
  </CardWrapper>
);

const CardContentWrapper = styled.div`
  height: 230px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${upTo('phone')} {
    height: auto;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const CardWrapper = styled.div<{ selected: boolean }>`
  padding: 8px;
  max-width: 400px;

  .ant-card-bordered {
    border-color: ${(props) => (props.selected ? RacemapColors.PaleBlue : RacemapColors.Gray)};
    box-shadow: ${(props) =>
      props.selected ? `0 3px 6px ${RacemapColors.PaleBlue}` : '0 1px 2px rgba(0, 0, 0, 0.1)'};
    transition: border-color 0.3s, box-shadow 0.3s;
  }
`;

const CreateButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
`;

const Label = ({
  text,
  icon,
  productId,
}: {
  text: string;
  icon: React.ReactNode;
  productId: StripeProducts | null;
}) => (
  <Flex justify="space-between" align="center">
    <Flex align="center" gap={10}>
      <IconWrapper>{icon}</IconWrapper>
      <Text strong>{text}</Text>
    </Flex>
    <ProductPrice productId={productId} textType="secondary" />
  </Flex>
);

const items: Array<NonNullable<CollapseProps['items']>[number] & { buttonText: string }> = [
  {
    key: EventTypes.REGULAR,
    label: <Label text="Single Map" icon={<IconRegular />} productId={StripeProducts.BASE_PRICE} />,
    buttonText: 'Create Free Trial',
    children: (
      <Text>
        Create and edit courses, add points of interest, manage participants. We recommend creating
        one map per race course—you can group them later if needed. Enhance your maps with add-ons
        like the leaderboard or API for additional features. All about customizing a single map in
        our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.REGULAR)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Text>
    ),
  },
  {
    key: EventTypes.SAFEMAP,
    label: (
      <Label text="Safemap" icon={<IconSafemap />} productId={StripeProducts.SAFEMAP_BASE_PRICE} />
    ),
    buttonText: 'Create Free Trial',
    children: (
      <Text>
        All critical safety information in one dashboard at a glance and in real-time. Receive
        alerts for SOS, crashes, off-track, or inactivity. Accessible only with a RACEMAP account
        and designed for the responsible persons for safety management - not for public display. All
        about setting up a safemap map in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.SAFEMAP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Text>
    ),
  },
  {
    key: EventTypes.CONTEST_GROUP,
    buttonText: 'Create Free',
    label: <Label text="Group Map" icon={<IconContestGroup />} productId={null} />,
    children: (
      <Text>
        Combine multiple Single Maps - Marathon, Half Marathon, and 10K — under one URL with filters
        to quickly navigate each course. There is no activation fee for a Group Map. More
        information about Group Map in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.CONTEST_GROUP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Text>
    ),
  },
  {
    key: EventTypes.STAGE_GROUP,
    buttonText: 'Create Free',
    label: <Label text="Stages" icon={<IconStageGroup />} productId={null} />,
    children: (
      <Text>
        Unlike a Group Map, participants are tracked through all stages of an event sequentially.
        More information about Stages in our{' '}
        <DocuLink
          path={getEventTypeDocuLink(EventTypes.STAGE_GROUP)}
          onClick={(e) => e.stopPropagation()}
        />
        .
      </Text>
    ),
  },
];

const IconWrapper = styled.div`
  font-size: 1.4em;
  width: 1em;
  color: ${RacemapColors.PaleBlue}
`;
